<template>
  <Dialog
    v-model:visible="isOpen"
    modal
    header="What's New"
    :style="{ width: 'min(480px, 95vw)' }"
    :dismissable-mask="true"
    class="whats-new-dialog"
    @update:visible="onVisibleChange"
    @hide="onHide"
  >
    <p class="whats-new-intro">Here’s what changed since your last visit:</p>
    <ul class="whats-new-list">
      <li v-for="entry in newEntries" :key="entry.date + entry.text" class="whats-new-item">
        <span class="whats-new-date">{{ formatDate(entry.date) }}</span>
        <span class="whats-new-text" v-html="formatEntryText(entry.text)" />
      </li>
    </ul>
    <template #footer>
      <Button label="Got it" @click="close" />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import DOMPurify from 'dompurify';
import { Dialog, Button } from '@/components/primevue';
import { useChangelog } from '@/modules/admin/composables/useChangelog';
import type { ChangelogEntry } from '@/modules/admin/interfaces/ChangelogEntry';
import dayjs from 'dayjs';

const props = withDefaults(
  defineProps<{
    visible: boolean;
    newEntries?: ChangelogEntry[];
  }>(),
  { newEntries: () => [] }
);

const emit = defineEmits<{
  'update:visible': [value: boolean];
  close: [];
}>();

const { markAsSeen } = useChangelog();

const isOpen = computed({
  get: () => props.visible,
  set: (v) => emit('update:visible', v),
});

const newEntries = computed(() => props.newEntries);

function formatDate(dateStr: string): string {
  return dayjs(dateStr).format('MMM D, YYYY');
}

function formatEntryText(text: string): string {
  const html = text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/`([^`]+)`/g, '<code>$1</code>');
  return DOMPurify.sanitize(html, { ALLOWED_TAGS: ['strong', 'code'] });
}

function close() {
  isOpen.value = false;
}

function onVisibleChange(value: boolean) {
  if (!value) emit('close');
}

function onHide() {
  markAsSeen();
}
</script>

<style scoped lang="scss">
.whats-new-intro {
  color: var(--text-secondary);
  font-size: 14px;
  margin: 0 0 16px 0;
}

.whats-new-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 60vh;
  overflow-y: auto;
}

.whats-new-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 0;
  border-bottom: 1px solid var(--border-primary);

  &:last-child {
    border-bottom: none;
  }
}

.whats-new-date {
  font-size: 12px;
  font-weight: 600;
  color: var(--accent-primary);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.whats-new-text {
  font-size: 14px;
  color: var(--text-primary);
  line-height: 1.5;

  :deep(strong) {
    font-weight: 600;
  }

  :deep(code) {
    font-family: var(--font-mono, monospace);
    font-size: 0.9em;
    padding: 2px 6px;
    background: var(--bg-tertiary);
    border-radius: 4px;
  }
}

:deep(.p-dialog) {
  background: var(--bg-primary);
  color: var(--text-primary);
}

:deep(.p-dialog-header) {
  background: var(--bg-primary);
  border-bottom: 1px solid var(--border-primary);
}

:deep(.p-dialog-content) {
  background: var(--bg-primary);
}

:deep(.p-dialog-footer) {
  background: var(--bg-primary);
  border-top: 1px solid var(--border-primary);
}
</style>
